import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  addDoc,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const RequestsManagement = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [currentUser]);

  const fetchRequests = async () => {
    try {
      const requestsRef = collection(db, 'requests');
      const q = query(
        requestsRef,
        where('establishmentId', '==', currentUser.establishmentId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const requestsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
      }));
      setRequests(requestsList);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOpenDialog = (request) => {
    setSelectedRequest(request);
    setComment('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
    setComment('');
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        processedAt: new Date(),
        processedBy: currentUser.uid,
        comment: comment,
      });

      // Créer une notification pour l'utilisateur
      await addDoc(collection(db, 'notifications'), {
        userId: selectedRequest.userId,
        establishmentId: currentUser.establishmentId,
        title: `Votre demande a été ${newStatus === 'approved' ? 'approuvée' : 'refusée'}`,
        message: comment || `Votre demande a été ${newStatus === 'approved' ? 'approuvée' : 'refusée'} par l'administration`,
        type: newStatus === 'approved' ? 'success' : 'info',
        read: false,
        createdAt: new Date(),
      });

      handleCloseDialog();
      fetchRequests();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la demande:', error);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      try {
        await deleteDoc(doc(db, 'requests', requestId));
        fetchRequests();
      } catch (error) {
        console.error('Erreur lors de la suppression de la demande:', error);
      }
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'warning' },
      approved: { label: 'Approuvée', color: 'success' },
      rejected: { label: 'Refusée', color: 'error' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const filteredRequests = requests.filter(request => {
    switch (currentTab) {
      case 0: // Toutes
        return true;
      case 1: // En attente
        return request.status === 'pending';
      case 2: // Approuvées
        return request.status === 'approved';
      case 3: // Refusées
        return request.status === 'rejected';
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          Gestion des Demandes
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab label="Toutes" />
            <Tab label="En attente" />
            <Tab label="Approuvées" />
            <Tab label="Refusées" />
          </Tabs>
        </Paper>

        <Paper>
          <List>
            {filteredRequests.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="Aucune demande"
                  secondary="Il n'y a pas de demandes dans cette catégorie"
                />
              </ListItem>
            ) : (
              filteredRequests.map((request) => (
                <React.Fragment key={request.id}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        {request.status === 'pending' && (
                          <>
                            <IconButton
                              edge="end"
                              aria-label="approve"
                              onClick={() => handleOpenDialog(request)}
                              sx={{ mr: 1 }}
                            >
                              <CheckIcon color="success" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              aria-label="reject"
                              onClick={() => handleOpenDialog(request)}
                              sx={{ mr: 1 }}
                            >
                              <CloseIcon color="error" />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteRequest(request.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <AssignmentIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" sx={{ mr: 1 }}>
                            {request.title}
                          </Typography>
                          {getStatusChip(request.status)}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {request.description}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Soumise le{' '}
                            {request.createdAt
                              ? format(request.createdAt, "d MMMM yyyy 'à' HH:mm", {
                                  locale: fr,
                                })
                              : 'Date inconnue'}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Traiter la demande</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedRequest?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {selectedRequest?.description}
            </Typography>
            <TextField
              fullWidth
              label="Commentaire (optionnel)"
              multiline
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={() => handleStatusChange(selectedRequest?.id, 'rejected')}
            color="error"
            startIcon={<CloseIcon />}
          >
            Refuser
          </Button>
          <Button
            onClick={() => handleStatusChange(selectedRequest?.id, 'approved')}
            color="success"
            startIcon={<CheckIcon />}
            variant="contained"
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RequestsManagement;
